import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  FiLogOut, FiPlus, FiEdit2, FiTrash2, FiDownload, FiRefreshCw, FiExternalLink, FiX,
  FiBookOpen, FiVideo, FiMic,
} from 'react-icons/fi';
import SeoHead from '../components/SeoHead';
import { useAuth, TOKEN_KEY } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import './AdminPages.css';

const KINDS = [
  { id: 'blogs', label: 'Blogs', icon: FiBookOpen },
  { id: 'videos', label: 'Videos', icon: FiVideo },
  { id: 'webinars', label: 'Webinars', icon: FiMic },
];

const slugify = (s = '') =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const blankBlog = () => ({
  id: '',
  slug: '',
  title: '',
  category: '',
  author: 'THINKINGWORLD Editorial',
  date: new Date().toISOString().slice(0, 10),
  readTime: '5 min read',
  cover: '',
  excerpt: '',
  tags: [],
  content: [''],
});

const blankVideo = () => ({
  id: '',
  title: '',
  description: '',
  youtubeId: '',
  category: '',
  date: new Date().toISOString().slice(0, 10),
  duration: '',
});

const blankWebinar = () => ({
  id: '',
  title: '',
  description: '',
  speaker: 'Barani · Founder, THINKINGWORLD',
  date: new Date().toISOString().slice(0, 10),
  time: '18:00 IST',
  status: 'upcoming',
  registerUrl: '/contact',
  cover: '',
});

const buildBlank = (kind) => {
  if (kind === 'blogs') return blankBlog();
  if (kind === 'videos') return blankVideo();
  return blankWebinar();
};

const AdminDashboardPage = () => {
  const { isAuthed, ready, logout } = useAuth();
  const { blogs, videos, webinars, upsert, remove, exportJson, resetToDefaults, ready: contentReady } = useContent();
  const [kind, setKind] = useState('blogs');
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const list = kind === 'blogs' ? blogs : kind === 'videos' ? videos : webinars;

  const counts = useMemo(() => ({ blogs: blogs.length, videos: videos.length, webinars: webinars.length }), [blogs, videos, webinars]);

  if (!ready) return null;
  if (!isAuthed) return <Navigate to="/admin" replace />;

  const startNew = () => setEditing(buildBlank(kind));
  const startEdit = (item) => setEditing(JSON.parse(JSON.stringify(item)));
  const cancel = () => setEditing(null);

  const save = async (item) => {
    let prepared = { ...item };
    if (!prepared.id) {
      const seed = prepared.title || prepared.slug || prepared.id || `entry-${Date.now()}`;
      prepared.id = slugify(seed) + '-' + Math.random().toString(36).slice(2, 6);
    }
    if (kind === 'blogs') {
      if (!prepared.slug) prepared.slug = slugify(prepared.title || prepared.id);
      if (typeof prepared.tags === 'string') prepared.tags = prepared.tags.split(',').map((t) => t.trim()).filter(Boolean);
      if (typeof prepared.content === 'string') prepared.content = prepared.content.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
    }
    const res = await upsert(kind, prepared);
    if (res && !res.ok) {
      window.alert(`Saved on screen, but publishing failed: ${res.error}`);
      return;
    }
    setEditing(null);
  };

  const onDelete = async (item) => {
    if (window.confirm(`Delete "${item.title}"? This publishes the change to all visitors.`)) {
      const res = await remove(kind, item.id);
      if (res && !res.ok) window.alert(`Publishing failed: ${res.error}`);
    }
  };

  const onReset = async () => {
    if (window.confirm(`Reset all ${kind} back to the shipped defaults? This publishes to all visitors.`)) {
      const res = await resetToDefaults(kind);
      if (res && !res.ok) window.alert(`Publishing failed: ${res.error}`);
    }
  };

  const handleLogout = () => {
    logout();
    nav('/');
  };

  return (
    <>
      <SeoHead title="Admin dashboard" description="THINKINGWORLD admin." path="/admin/dashboard" />
      <div className="admin-shell">
        <header className="admin-top">
          <div className="admin-top-left">
            <Link to="/" className="admin-brand">
              <span className="logo-mark logo-mark--image"><img src="/img/company-logo.png" alt="THINKINGWORLD" /></span>
              <span>THINKINGWORLD · <span className="logo-faint">Admin</span></span>
            </Link>
            <nav className="admin-tabs">
              {KINDS.map((k) => {
                const Icon = k.icon;
                return (
                  <button
                    key={k.id}
                    type="button"
                    className={`admin-tab ${k.id === kind ? 'admin-tab--active' : ''}`}
                    onClick={() => { setKind(k.id); setEditing(null); }}
                  >
                    <Icon /> {k.label}
                    <span className="admin-tab-count">{counts[k.id]}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="admin-top-right">
            <Link to="/" className="admin-link"><FiExternalLink /> View site</Link>
            <button type="button" className="btn btn-dark admin-logout" onClick={handleLogout}>
              <FiLogOut /> Logout
            </button>
          </div>
        </header>

        <div className="admin-actions">
          <button type="button" className="btn btn-accent" onClick={startNew}>
            <FiPlus /> New {kind.slice(0, -1)}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => exportJson(kind)}>
            <FiDownload /> Download {kind}.json
          </button>
          <button type="button" className="btn btn-ghost" onClick={onReset}>
            <FiRefreshCw /> Reset to defaults
          </button>
        </div>

        <div className="admin-help">
          <strong>How publishing works:</strong> clicking <em>Save</em> publishes instantly to all visitors — no deploy needed.
          <em>Download {kind}.json</em> is just an optional backup of the current list.
        </div>

        {!contentReady && <p className="admin-empty">Loading…</p>}

        {contentReady && list.length === 0 && (
          <p className="admin-empty">No {kind} yet. Click "New" to create one.</p>
        )}

        <div className="admin-list">
          {list.map((item) => (
            <article key={item.id} className="admin-row">
              <div className="admin-row-thumb">
                {kind === 'videos' ? (
                  <img
                    src={`https://img.youtube.com/vi/${item.youtubeId}/default.jpg`}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : item.cover ? (
                  <img src={item.cover} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div className="admin-row-thumb-placeholder" />
                )}
              </div>
              <div className="admin-row-body">
                <div className="admin-row-meta">
                  {item.category && <span className="admin-pill">{item.category}</span>}
                  {item.status && <span className="admin-pill admin-pill--muted">{item.status}</span>}
                  {item.date && <span>{item.date}</span>}
                  {item.slug && <span className="admin-row-slug">/{item.slug}</span>}
                </div>
                <h3>{item.title || '(untitled)'}</h3>
                <p>{item.excerpt || item.description}</p>
              </div>
              <div className="admin-row-actions">
                {kind === 'blogs' && item.slug && (
                  <Link to={`/resources/blog/${item.slug}`} className="admin-icon-btn" title="Preview">
                    <FiExternalLink />
                  </Link>
                )}
                <button type="button" className="admin-icon-btn" onClick={() => startEdit(item)} title="Edit">
                  <FiEdit2 />
                </button>
                <button type="button" className="admin-icon-btn admin-icon-btn--danger" onClick={() => onDelete(item)} title="Delete">
                  <FiTrash2 />
                </button>
              </div>
            </article>
          ))}
        </div>

        {editing && (
          <AdminEditor
            kind={kind}
            initial={editing}
            onCancel={cancel}
            onSave={save}
          />
        )}
      </div>
    </>
  );
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const AdminEditor = ({ kind, initial, onCancel, onSave }) => {
  const [item, setItem] = useState(() => {
    const seed = { ...initial };
    if (kind === 'blogs') {
      if (Array.isArray(seed.content)) seed.content = seed.content.join('\n\n');
      if (Array.isArray(seed.tags)) seed.tags = seed.tags.join(', ');
    }
    return seed;
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const set = (k) => (e) => setItem({ ...item, [k]: e.target.value });

  // Upload the image to Netlify Blobs and store only the returned URL on the
  // post — the file bytes live in the store, not inside the blog data.
  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (file.size > 4.5 * 1024 * 1024) {
      setUploadError('Image is larger than 4.5 MB. Please choose a smaller one.');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const dataBase64 = String(dataUrl).split(',')[1] || '';
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch('/api/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filename: file.name, contentType: file.type, dataBase64 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        setItem((prev) => ({ ...prev, cover: data.url }));
      } else {
        setUploadError(data.error || `Upload failed (HTTP ${res.status})`);
      }
    } catch {
      setUploadError('Could not reach the upload service. Check your connection.');
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (uploading) return;
    onSave(item);
  };

  return (
    <div className="admin-modal" onClick={onCancel}>
      <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onCancel} aria-label="Close">
          <FiX />
        </button>
        <h2>{initial.id ? 'Edit' : 'New'} {kind.slice(0, -1)}</h2>

        <form className="admin-form" onSubmit={submit}>
          <label>
            <span>Title *</span>
            <input type="text" required value={item.title || ''} onChange={set('title')} />
          </label>

          {kind === 'blogs' && (
            <>
              <div className="admin-form-row">
                <label>
                  <span>Slug (URL)</span>
                  <input type="text" value={item.slug || ''} onChange={set('slug')} placeholder="auto-from-title" />
                </label>
                <label>
                  <span>Category</span>
                  <input type="text" value={item.category || ''} onChange={set('category')} placeholder="Web Development" />
                </label>
              </div>
              <div className="admin-form-row">
                <label>
                  <span>Author</span>
                  <input type="text" value={item.author || ''} onChange={set('author')} />
                </label>
                <label>
                  <span>Date</span>
                  <input type="date" value={item.date || ''} onChange={set('date')} />
                </label>
                <label>
                  <span>Read time</span>
                  <input type="text" value={item.readTime || ''} onChange={set('readTime')} placeholder="6 min read" />
                </label>
              </div>
              <label>
                <span>Excerpt</span>
                <textarea rows="2" value={item.excerpt || ''} onChange={set('excerpt')} />
              </label>
              <label>
                <span>Tags (comma-separated)</span>
                <input type="text" value={item.tags || ''} onChange={set('tags')} placeholder="seo, web, india" />
              </label>
              <label>
                <span>Content (separate paragraphs with a blank line)</span>
                <textarea rows="10" value={item.content || ''} onChange={set('content')} />
              </label>
            </>
          )}

          {kind === 'videos' && (
            <>
              <div className="admin-form-row">
                <label>
                  <span>YouTube URL or ID *</span>
                  <input type="text" required value={item.youtubeId || ''} onChange={set('youtubeId')} placeholder="https://youtu.be/dQw4w9WgXcQ" />
                </label>
                <label>
                  <span>Category</span>
                  <input type="text" value={item.category || ''} onChange={set('category')} placeholder="SEO" />
                </label>
              </div>
              <div className="admin-form-row">
                <label>
                  <span>Date</span>
                  <input type="date" value={item.date || ''} onChange={set('date')} />
                </label>
                <label>
                  <span>Duration</span>
                  <input type="text" value={item.duration || ''} onChange={set('duration')} placeholder="08:15" />
                </label>
              </div>
              <label>
                <span>Description</span>
                <textarea rows="4" value={item.description || ''} onChange={set('description')} />
              </label>
            </>
          )}

          {kind === 'webinars' && (
            <>
              <div className="admin-form-row">
                <label>
                  <span>Speaker</span>
                  <input type="text" value={item.speaker || ''} onChange={set('speaker')} />
                </label>
                <label>
                  <span>Status</span>
                  <select value={item.status || 'upcoming'} onChange={set('status')}>
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past</option>
                  </select>
                </label>
              </div>
              <div className="admin-form-row">
                <label>
                  <span>Date</span>
                  <input type="date" value={item.date || ''} onChange={set('date')} />
                </label>
                <label>
                  <span>Time</span>
                  <input type="text" value={item.time || ''} onChange={set('time')} placeholder="18:00 IST" />
                </label>
                <label>
                  <span>Register URL</span>
                  <input type="text" value={item.registerUrl || ''} onChange={set('registerUrl')} placeholder="/contact" />
                </label>
              </div>
              <label>
                <span>Description</span>
                <textarea rows="4" value={item.description || ''} onChange={set('description')} />
              </label>
            </>
          )}

          {(kind === 'blogs' || kind === 'webinars') && (
            <>
              <label>
                <span>Cover image URL</span>
                <input type="text" value={item.cover || ''} onChange={set('cover')} placeholder="/img/blog/your-image.jpg or https://…" />
              </label>
              <label className="admin-upload">
                <span>…or upload a cover image (stored on the server, max 4.5 MB)</span>
                <input type="file" accept="image/*" onChange={onCoverFile} disabled={uploading} />
              </label>
              {uploading && <p className="admin-upload-status">Uploading image…</p>}
              {uploadError && <p className="admin-upload-status admin-upload-status--error">{uploadError}</p>}
              {item.cover && (
                <div className="admin-cover-preview">
                  <img src={item.cover} alt="cover preview" />
                </div>
              )}
            </>
          )}

          <div className="admin-form-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-accent">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
